/**
 * Cookie utilities.
 *
 * These are also defined as inline globals in templates/components/scripts/cookie-utils.html
 * so they are available before the webpack bundle loads. The inline versions are the
 * authoritative runtime source; these exports exist for webpack module consumers.
 */

export function setCookie(name, value, days, domain, sameSite = 'Lax', secure = true) {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }

    let cookieStr = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=${sameSite}`;
    if (domain) {
        cookieStr += `; domain=${domain}`;
    }
    if (secure) {
        cookieStr += '; Secure';
    }
    document.cookie = cookieStr;
}

export function getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        const c = cookies[i].trim();
        if (c.indexOf(nameEQ) === 0) {
            return decodeURIComponent(c.substring(nameEQ.length));
        }
    }
    return null;
}

export function deleteCookie(name, domain) {
    setCookie(name, '', -1, domain);
}

export function parseConsentCookieStringToObject(queryString) {
    if (!queryString) return {};

    const params = {};
    const pairs = queryString.split('&');
    for (const pair of pairs) {
        const keyValue = pair.split('=');
        if (keyValue.length === 2) {
            let key = keyValue[0];
            let value = keyValue[1];
            if (value === 'true')       value = true;
            else if (value === 'false') value = false;
            else if (value === 'null' || value === '') value = null;
            params[key] = value;
        }
    }
    return params;
}

export function consentObject() {
    let cookie = getCookie('wscrCookieConsent');
    cookie = parseConsentCookieStringToObject(cookie);
    if (cookie == null) {
        cookie = window.cookiePolicyConsent;
    }
    return cookie;
}

export function cleanMetaData(metaData) {
    const alwaysInclude = ['is_logged_in'];
    for (const key in metaData) {
        if (Object.prototype.hasOwnProperty.call(metaData, key)) {
            const value = metaData[key];
            const isEmpty =
                value === null ||
                value === undefined ||
                (typeof value === 'string' && value.trim() === '') ||
                (Array.isArray(value) && value.length === 0);
            if (isEmpty && !alwaysInclude.includes(key)) {
                delete metaData[key];
            }
        }
    }
    return metaData;
}

/**
 * The customer's Loyalty Tier as a spreadable analytics field.
 *
 * Source is the BigCommerce customer attribute `Loyalty Tier` (entityId 2), read at
 * RENDER time into window.LG_PAGE_DATA.gqlCustomer by lg-page-data.html — the theme's
 * global config.json `resources.gql` and all 12 self-declaring page templates select it.
 * There is no client-side fetch and no fallback: on a page that somehow lacks the field,
 * this returns {} and the key is simply absent.
 *
 * Returns a SPREADABLE object so the omit-when-empty rule lives in exactly one place.
 * That matters most for sendInteractionEvent, which skips cleanMetaData entirely — an
 * unconditional key there would ship `loyalty_program: ''` for every guest.
 *
 * Reads the bare global rather than `window.LG_PAGE_DATA` so the function is callable in
 * the `node` Vitest environment, matching resolveBreadcrumbFields in ga4-events.js.
 *
 * @returns {{ loyalty_program?: string }} `{ loyalty_program: tier }` or `{}`
 */
export function loyaltyProgramField() {
    const lgPageData = typeof LG_PAGE_DATA !== 'undefined' ? LG_PAGE_DATA : undefined;
    const tier = lgPageData?.gqlCustomer?.attributes?.loyaltyTier?.value;
    return tier ? { loyalty_program: tier } : {};
}

export function hasValidExpectedKey(obj, keys) {
    if (!obj || typeof obj !== 'object') return false;
    const allowed = new Set(keys);
    return Object.keys(obj).some(key => {
        if (!allowed.has(key)) return false;
        const val = obj[key];
        if (val == null) return false;
        if (typeof val === 'string' && val.trim() === '') return false;
        return true;
    });
}
